import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type Theme = "light" | "dark";
interface ThemeContextValue {
  theme: Theme;
  toggle: () => void;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// True when the SPA is hosted inside the analytics dashboard shell. On
// analytics.*, the outer #themeToggle button in the shared header owns the
// user's light/dark preference and stores it in localStorage['sr-theme'].
// We don't want ThemeProvider to fight that -- we mirror it instead.
function inAnalyticsShell(): boolean {
  try {
    const h = window.location.hostname || "";
    return h.indexOf("analytics.") === 0 || h === "analytics.sunrental.com";
  } catch {
    return false;
  }
}

function readAnalyticsTheme(): Theme {
  try {
    return localStorage.getItem("sr-theme") === "light" ? "light" : "dark";
  } catch {
    return "light";
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Customer signing site: dark mode is disabled, always light, class never
  // applied. Analytics-embedded: mirror the outer shell's data-theme /
  // sr-theme so the shared #themeToggle button controls both surfaces.
  const embedded = typeof window !== "undefined" && inAnalyticsShell();
  const [theme, setTheme] = useState<Theme>(() =>
    embedded ? readAnalyticsTheme() : "light"
  );

  useEffect(() => {
    if (!embedded) {
      document.documentElement.classList.remove("dark");
      return;
    }
    // Sync the Tailwind .dark class to whatever the outer shell picked.
    // The inline bootstrap in index.html already did this once before paint;
    // this keeps it in sync as the user toggles.
    const html = document.documentElement;
    const apply = () => {
      const t = readAnalyticsTheme();
      setTheme(t);
      if (t === "light") html.classList.remove("dark");
      else html.classList.add("dark");
    };
    apply();
    // The shared header toggle writes to localStorage, but same-tab writes
    // don't fire the "storage" event. Observe the <html> data-theme attribute
    // instead -- nav.js mutates it directly on click.
    const obs = new MutationObserver(apply);
    obs.observe(html, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, [embedded]);

  const persist = (t: Theme) => {
    setTheme(t);
    if (!embedded) return;
    const html = document.documentElement;
    try {
      if (t === "light") {
        localStorage.setItem("sr-theme", "light");
        html.setAttribute("data-theme", "light");
        html.classList.remove("dark");
      } else {
        localStorage.removeItem("sr-theme");
        html.removeAttribute("data-theme");
        html.classList.add("dark");
      }
    } catch {}
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme: persist,
        toggle: () => persist(theme === "light" ? "dark" : "light"),
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
